<template>
    <div>
        <UForm
            ref="form"
            :schema="schema"
            :state="subAccountState"
            :validate-on="['blur', 'input']"
            @submit="onSubmit"
            @error="onError"
        >
            <div class="flex justify-between">
                <div class="text-xl font-semibold" data-cy="entity-name">
                    <template v-if="subAccount?.name">
                        <span v-if="(subAccount as SubAccount).subAccountNo">{{ (subAccount as SubAccount).subAccountNo }} - </span> {{ subAccount.name }}
                    </template>
                    <template v-else>
                        Ny {{ isSupplier() ? 'leverandør' : 'kunde' }}
                    </template>
                </div>
                <UButton
                    v-if="!isNew && !isSupplier()"
                    icon="i-mdi-format-list-group-plus"
                    label="Ny faktura"
                    variant="outline"
                    data-cy="new-invoice-button"
                    class="ml-auto"
                    @click="newInvoice && newInvoice()"
                />
                <UButton
                    v-if="isNew"
                    icon="i-ph-x"
                    variant="ghost"
                    color="neutral"
                    class="ml-auto"
                    :data-cy="SubAccountCy.ButtonCancel"
                    @click="emit('hideEditor')"
                />
                <UDropdownMenu
                    v-if="!isNew"
                    size="sm"
                    :items="menuItems"
                >
                    <UButton
                        icon="i-ph-dots-three-vertical-bold"
                        size="md"
                        variant="ghost"
                        color="neutral"
                        @click.stop
                    />
                    <template #delete>
                        <UTooltip
                            :text="isSupplier() ? 'Leverandøren har transaksjoner og kan ikke slettes' : 'Kunden har transaksjoner og kan ikke slettes'"
                            :disabled="isDeletable"
                            :content="{ sideOffset: 16 }"
                        >
                            <span class="flex items-center gap-1.5 w-full pointer-events-auto">
                                <UIcon name="i-ph-trash" class="shrink-0 size-4" />
                                {{ isSupplier() ? 'Slett leverandør' : 'Slett kunde' }}
                            </span>
                        </UTooltip>
                    </template>
                </UDropdownMenu>
            </div>

            <div class="max-w-6xl h-full overflow-auto mb-8">
                <UFormField
                    class="text-sm mt-6 mb-2 w-fit"
                    :label="isSupplier() ? 'Leverandørtype' : 'Kundetype'"
                >
                    <URadioGroup
                        v-model="subAccountState.isBusiness"
                        :items="[{ label: $t('sub-account-editor.BUSINESS'), value: true },{ label: $t('sub-account-editor.PERSONAL'), value: false }]"
                        orientation="vertical"
                        class="capitalize"
                        @update:model-value="setIsBusiness"
                    >
                        <template #label="{ item }">
                            <span :data-cy="item.value ? 'business-button' : 'private-button'">
                                {{ item.label }}
                            </span>
                        </template>
                    </URadioGroup>
                </UFormField>

                <div class="text-lg font-semibold mt-4 mb-2 col-span-6 flex gap-1">
                    <span>{{ isSupplier() ? 'Leverandørinformasjon' : 'Kundeinformasjon' }}</span>
                    <USeparator />
                </div>

                <div class="grid grid-cols-6 gap-2 mt-2">
                    <template
                        v-for="field in fields"
                        :key="field.name"
                    >
                        <UFormField
                            v-if="!field.condition || field.condition(subAccountState)"
                            :name="field.name"
                            :label="field.label"
                            :class="field.class || ''"
                            :ui="{ container: field.class || '' }"
                        >
                            <Component
                                v-bind="field.props"
                                :is="field.component"
                                v-if="field.component"
                                :ref="(el: any) => { if (field.name === 'name') brregNameInput = el }"
                                v-model="useModel(field.name).value"
                                :data-cy="field.dataCy || ''"
                                v-on="field.events || {}"
                            />
                            <UInput
                                v-else
                                v-model="useModel(field.name).value"
                                size="lg"
                                class="w-full"
                                autocomplete="off"
                                :type="field.type || 'text'"
                                :data-cy="field.dataCy || ''"
                            />

                            <template #error="{ error }">
                                <span class="text-sm text-red-500">{{ error ? $t(error?.toString()) : '' }}</span>
                            </template>
                        </UFormField>
                    </template>

                    <div v-if="!isSupplier() && kraviaActive" class="row-start-6 col-span-full grid grid-cols-6">
                        <div class="text-lg font-semibold mb-2 col-span-6 flex gap-1">
                            <span>Betalingsinnstillinger</span>
                            <USeparator />
                        </div>
                        <UFormField
                            name="customerDebtCollectionSettings.customerSettingsEnabled"
                            class="flex flex-row-reverse items-center justify-end col-span-full"
                            label="Overstyr inkassoinnstillinger"
                        >
                            <USwitch
                                v-model="useModel('customerDebtCollectionSettings.customerSettingsEnabled').value"
                                name="customerDebtCollectionSettings.customerSettingsEnabled"
                                data-cy="debt-collection-settings-switch"
                                class="mr-2"
                                size="lg"
                            />
                        </UFormField>

                        <UFormField
                            v-if="debtCollectionIsOverridden()"
                            class="col-span-2 mt-2"
                            name="customerDebtCollectionSettings.reminderType"
                            label="Påminnelsestype"
                        >
                            <KraviaReminderOptionSelector v-model="useModel('customerDebtCollectionSettings.reminderType').value"/>
                        </UFormField>

                        <UFormField
                            v-if="debtCollectionIsOverridden()"
                            class="col-span-2 mt-2 ml-2"
                            name="customerDebtCollectionSettings.intervalDays"
                            label="Dager før påminnelse"
                        >
                            <UInput
                                v-model="useModel('customerDebtCollectionSettings.intervalDays').value"
                                type="number"
                                autocomplete="off"
                                size="lg"
                                class="w-full"
                                data-cy="reminder-days-input"
                            />
                        </UFormField>
                    </div>

                    <UInput
                        v-for="field in debtCollectionHiddenFields"
                        :key="field.name"
                        v-model="useModel(field.name).value"
                        class="hidden"
                    />

                    <div class="row-start-7 col-span-full grid grid-cols-6">
                        <div class="text-lg font-semibold mt-4 mb-2 col-span-6 flex gap-1">
                            <span>Annet</span>
                            <USeparator />
                        </div>
                        <UFormField name="notes" label="Notater" class="row-start-7 col-span-full">
                            <UTextarea
                                v-model="useModel('notes').value"
                                :rows="3"
                                autoresize
                                class="w-full"
                                data-cy="notes-textarea"
                                placeholder="Skriv inn notater her..."
                            />
                        </UFormField>
                    </div>
                </div>
                <div v-if="fulfillmentModel" class="pt-2">
                    <fulfillment-selector v-model="fulfillmentModel.fulfillOnSuccess" :assignment-no="fulfillmentModel?.assignmentNo || 0" />
                </div>
                <UAlert
                    v-for="(error, index) in errors"
                    :key="index"
                    icon="i-heroicons-exclamation-triangle-20-solid"
                    color="red"
                    variant="subtle"
                    class="mt-2"
                    :title="`${error.path.join('->')}: ${error.message}`"
                />
                <div v-if="orgNoExistsForCustomer && !isSupplier()" class="text-error flex gap-1 items-start mt-2">
                    <i class="i-ph-info text-xl mt-0.5" /> En kunde med org.nr <b>{{ subAccountState.orgNo }}</b> finnes allerede: <b>{{ orgNoExistsForCustomer.name }}</b>
                </div>
                <div v-if="orgNoExistsForSupplier && isSupplier()" class="text-error flex gap-1 items-start mt-2">
                    <i class="i-ph-info text-xl mt-0.5" /> En leverandør med org.nr <b>{{ subAccountState.orgNo }}</b> finnes allerede: <b>{{ orgNoExistsForSupplier.name }}</b>
                </div>
            </div>

            <div class="flex gap-2 mt-2 justify-end">
                <div class="space-x-2">
                    <UButton
                        type="reset"
                        data-cy="subaccount-reset-button"
                        label="Avbryt"
                        variant="subtle"
                        color="neutral"
                        @click="onReset"
                    />
                    <UButton
                        type="submit"
                        :data-cy="SubAccountCy.ButtonSubmit"
                        :label="(isNew ? 'Opprett' : 'Lagre') + (isSupplier() ? ' leverandør' : ' kunde')"
                        :disabled="!isFormValid"
                    />
                </div>
                <SubAccountConfirmPopup
                    v-model:isVisible="showConfirmPopup"
                    :is-customer="!isSupplier()"
                    :entity-name="subAccountState.name"
                    @handle-confirm="handleDelete"
                />
            </div>
        </UForm>
    </div>
</template>

<script setup lang="ts">
import {Component as VueComponent, computed, ComputedRef, inject, nextTick, onMounted, PropType, reactive, Ref, ref, watch} from 'vue'
import {type ZodObject} from 'zod'
import {NewSubAccount, NewSubAccountSchema, SubAccount, SubAccountSchema, SUPPLIER} from '@/subaccount/types/SubAccount'
import CountrySelector from '@/subaccount/components/CountrySelector.vue'
import BrregSearchNew from '@/debug/BrregSearchNew.vue'
import BookKeepingAccountPicker from '@/bookkeeping-account/BookKeepingAccountPicker.vue'
import KraviaReminderOptionSelector from '@/debt-collection/KraviaReminderOptionSelector.vue'
import {DropdownMenuItem, Form, FormSubmitEvent} from '@nuxt/ui'
import type {Organization} from '@tritt/brg-search-client-ts/lib/types'
import {notifyError, notifySuccess} from '@/services/infrastructure/notification.service'
import {get, set} from 'lodash'
import {createSubAccount, deleteSubAccount, fetchCustomers, fetchSuppliers, findCustomerByOrgNo, findSupplierByOrgNo, SUB_ACCOUNT_CREATED, updateSubAccount, useSubAccountForm} from '@/subaccount/subAccount.service'
import {deCapsifyCompanyName} from '@/subaccount/subAccount.utils'
import {findNorwegianPostalInfo} from '@/services/infrastructure/patron.service'
import SubAccountConfirmPopup from '@/subaccount/components/SubAccountConfirmPopup.vue'
import hotkeys from 'hotkeys-js'
import {SubAccountCy} from '@/subaccount/components/SubAccountCy'
import {Fulfillment} from '@/assignments/Assignment.type'
import FulfillmentSelector from '@/assignments/FulfillmentSelector.vue'
import emitter from '@/services/infrastructure/emitter'
import {CUSTOMERS, SUPPLIERS} from '@/subaccount/index'
import {usePanelManager} from '@tritt/multipanel'
import {useSetting} from '@/setting/setting.service'

type FormError = {
    message: string,
    path: string[]
}

const props = defineProps({
    subAccount: {
        type: Object as PropType<SubAccount | NewSubAccount>,
        required: true
    },
    newInvoice: {
        type: Function as PropType<() => void>,
        required: false
    },
    fulfillment: {
        type: Object as PropType<Fulfillment>
    }
})

const emit = defineEmits(['updated', 'created', 'deleted', 'hideEditor'])
const { setPanelPath } = usePanelManager()
const panelId: Ref<number> | undefined = inject('panelId')

const subAccountState = reactive<SubAccount | NewSubAccount>(JSON.parse(JSON.stringify(props.subAccount)))

const brregNameInput = ref<any>(null)
const form = ref<Form<typeof subAccountState> | null>(null)
const fulfillmentModel = ref(props.fulfillment ? props.fulfillment : undefined)

const errors = ref<FormError[]>([])
const tempOrgNo = ref('')
const showConfirmPopup = ref(false)
const isDeletable = ref(false)

const isNew = computed(() => !((props.subAccount as SubAccount).uuid))
const schema = computed(() => isNew.value ? NewSubAccountSchema : SubAccountSchema) as ComputedRef<ZodObject<any>>

const isFormValid = computed(() => {
    if (!schema.value) return false

    const result = schema.value.safeParse(subAccountState)
    return result.success
})

const useModel = (path: string) => {
    return computed({
        get: () => get(subAccountState, path),
        set: (value) => set(subAccountState, path, value)
    })
}

type Field = {
    divider?: boolean,
    name: string,
    icon?: string,
    label?: string,
    class?: string,
    doubleWidth?: boolean,
    condition?: (state: SubAccount | NewSubAccount) => boolean,
    component?: VueComponent,
    type?: string,
    props?: Record<string, any>,
    events?: Record<string, Function>,
    dataCy?: string
}

const isSupplier = () => subAccountState.relationType === SUPPLIER

const fields = computed<Array<Field>>(() => [
    {name: 'uuid', class: 'hidden'},
    {name: 'relationType', class: 'hidden'},
    {name: 'subAccountNo', class: 'hidden'},
    {name: 'name', dataCy: 'name-input', label: 'Navn', icon: 'i-ph-user-circle', class: 'col-span-4', ...(subAccountState.isBusiness ? {component: BrregSearchNew, events: {orgSelected: onOrgSelected}} : {})},
    {name: 'orgNo', dataCy: 'org-no-input', label: 'Organisasjonsnummer', icon: 'i-ph-buildings-fill', class: 'col-span-2', condition: (state) => !!state?.isBusiness},

    {name: 'contactInfo.name', dataCy: 'contact-name-input', label: 'Kontaktperson', icon: 'i-ph-user-circle', class: 'row-start-2 col-span-3'},
    {name: 'contactInfo.phoneNumber', dataCy: 'phone-no-input', label: 'Telefon', icon: 'i-ph-phone-fill', class: 'row-start-2'},
    {name: 'contactInfo.email', dataCy: 'email-input', label: 'E-post', icon: 'i-ph-envelope-simple', class: 'row-start-2 col-span-2'},

    {name: 'address.addressLine', dataCy: 'address-input', label: 'Adresse', icon: 'i-ph-map-pin-fill', class: 'row-start-3 col-span-2'},
    {name: 'address.postalCode', dataCy: 'postal-code-input', label: 'Postnr.', icon: 'i-mdi-numeric', class: 'row-start-3'},
    {name: 'address.postalLocation', dataCy: 'postal-location-input', label: 'Poststed', icon: 'i-ph-map-pin', class: 'row-start-3 col-span-2'},
    {name: 'address.countryCode', label: 'Land', dataCy: 'country-input', component: CountrySelector, class: 'row-start-3'},
    {
        name: 'metadata.suggestions.expenseAccount',
        dataCy: 'suggested-expense-account-picker',
        label: 'Regnskapskonto',
        icon: 'i-ph-calculator',
        class: 'row-start-4 col-span-1 @[800px]:col-span-2',
        condition: isSupplier,
        component: BookKeepingAccountPicker
    },
    {name: 'bban', dataCy: 'bban-input', label: 'Bankkontonummer', icon: 'i-ph-bank', class: 'row-start-5 col-span-2', condition: isSupplier},
    {name: 'bic', dataCy: 'bic-input', label: 'BIC', icon: 'i-ph-bank', class: 'row-start-5 col-span-2', condition: isSupplier},
    {name: 'iban', dataCy: 'iban-input', label: 'IBAN', icon: 'i-ph-bank', class: 'row-start-5 col-span-2', condition: isSupplier}
])

const menuItems = computed<DropdownMenuItem[]>(() => {
    return [
        {
            label: isSupplier() ? 'Slett leverandør' : 'Slett kunde',
            slot: 'delete',
            value: 'delete',
            icon: 'i-ph-trash',
            color: 'error',
            'data-cy': 'delete-button',
            disabled: !isDeletable.value,
            onSelect: onDeleteClick,
        }
    ]
})

const debtCollectionHiddenFields = computed<Field[]>(() => {
    return [
        {name: 'customerDebtCollectionSettings.subAccountUuid', class: 'hidden'}
    ]
})

const debtCollectionIsOverridden = () => {
    return subAccountState.customerDebtCollectionSettings?.customerSettingsEnabled
}

const onSubmit = async (event: FormSubmitEvent<Record<string, unknown>>) => {
    if (!isFormValid.value) {
        console.error('Attempting to save, but form is not valid!')
        return
    }

    const subAccount = event.data as SubAccount

    if (isNew.value) {
        await createSubAccount(subAccount, fulfillmentModel.value)
            .then((newSacc: SubAccount) => {
                emit('created', newSacc)
                emitter.emit(SUB_ACCOUNT_CREATED)
                notifySuccess({title: 'Opprettet', description: `${!isSupplier() ? 'Kunden' : 'Leverandøren'} ${newSacc.name} ble opprettet.`})
            })
    } else {
        await updateSubAccount(subAccount)
            .then((updatedSacc: SubAccount) => {
                emit('updated', updatedSacc)
                notifySuccess({title: 'Oppdatert', description: `${!isSupplier() ? 'Kunde' : 'Leverandør'} ${updatedSacc.name} ble oppdatert.`})
            })
    }

}

const onError = (event: any) => {
    errors.value = event.errors
    notifyError({title: 'Feil i skjema', description: 'Vennligst sjekk feltene og prøv igjen.'})
}

const handleDelete = async () => {
    const subAccount = subAccountState as SubAccount

    try {
        await deleteSubAccount(subAccount.uuid)
        emit('deleted')
        notifySuccess({title: `${subAccount.name || 'Kontoen'} er slettet.`, description: ''})
        showConfirmPopup.value = false

        isSupplier() ? await fetchSuppliers() : await fetchCustomers()
    } catch (e) {
        notifyError({title: 'Feil ved sletting', description: 'Kunne ikke slette kontoen.'})
    }

}

const onDeleteClick = () => {
    showConfirmPopup.value = true
}

const onReset = () => {
    // Object.assign necessary for keeping the reactivity intact (subAccountState is not a ref or a computedRef)
    Object.assign(subAccountState, JSON.parse(JSON.stringify(props.subAccount)))
    errors.value = []
    form.value?.clear()

    if(isNew.value) {
        emit('hideEditor')
    } else {
        const path = isSupplier() ? SUPPLIERS : CUSTOMERS
        setPanelPath({...path, panelId: panelId?.value})
    }
}

const onOrgSelected = async (org: Organization) => {
    if (org) {
        set(subAccountState, 'name', deCapsifyCompanyName(org.name))
        set(subAccountState, 'orgNo', org.number)
        set(subAccountState, 'address.addressLine', org.billingAddress?.streetAddress.join(', '))
        set(subAccountState, 'address.postalCode', org.billingAddress?.postalCode)
        set(subAccountState, 'address.postalLocation', org.billingAddress?.postalPlace)
        set(subAccountState, 'address.countryCode', org.billingAddress?.countryCode)

        const postalCode = org.billingAddress?.postalCode
        if (postalCode) {
            const postalInfo = await findNorwegianPostalInfo(postalCode)
            if (postalInfo) {
                set(subAccountState, 'address.postalLocation', postalInfo.postalPlace)
                set(subAccountState, 'address.countryCode', postalInfo.countryCode)
            }
        }
    }
}

const setIsBusiness = (value: boolean) => {
    subAccountState.isBusiness = value
    if (!value) {
        tempOrgNo.value = subAccountState.orgNo || ''
        set(subAccountState, 'orgNo', '')
    } else if (tempOrgNo.value) {
        set(subAccountState, 'orgNo', tempOrgNo.value)
    }
}

const setBrregFocus = async () => {
    if (!isNew.value) return
    await nextTick()
    brregNameInput.value?.focus()
}

defineExpose({
    focus: setBrregFocus
})

const {setting: kraviaCreditorIdSetting} = useSetting<string>('KRAVIA_CREDITOR_ID')
const kraviaActive = computed(() => !!kraviaCreditorIdSetting.value && kraviaCreditorIdSetting.value.value !== null)

const orgNoExistsForCustomer = computed(() => {
    if (!isNew.value || !subAccountState.orgNo) return undefined
    return findCustomerByOrgNo(subAccountState.orgNo)
})

const orgNoExistsForSupplier = computed(() => {
    if (!isNew.value || !subAccountState.orgNo) return undefined
    return findSupplierByOrgNo(subAccountState.orgNo)
})

watch(() => subAccountState.address?.postalCode, async (postalCode) => {
    if (postalCode && postalCode.length === 4) {
        const postalInfo = await findNorwegianPostalInfo(postalCode)
        if (postalInfo) {
            set(subAccountState, 'address.postalLocation', postalInfo.postalPlace)
            set(subAccountState, 'address.countryCode', postalInfo.countryCode)
        }
    }
})

watch(subAccountState, () => {
    errors.value = []
}, {deep: true})

onMounted(async () => {
    hotkeys('esc', () => emit('hideEditor'))
    if (!isNew.value) {
        const {fetchTransactionCountForSubAccount} = useSubAccountForm(props.subAccount)
        const tlCount = await fetchTransactionCountForSubAccount()
        isDeletable.value = tlCount === 0
    }
})

</script>

<style scoped>

</style>